import { suggestionsQueryApi } from 'api/suggestions/suggestionsQueryApi';
import { store } from 'createStore';
import type * as go from 'gojs';
import type { AvailablePort } from 'gojs/types';
import { isUnitType } from 'unitData/types';
import { SMART_SELECT_SUGGESTION_FETCH_COUNT } from './smartSelectConstants';
import type { SmartSelectRecommendedOption } from './smartSelectRecommendations';
import {
  getSmartSelectRecommendedOptions,
  mapNodeSuggestionsToCandidates,
} from './smartSelectRecommendations';

const nodeSmartSelectRecommendedOptions = new WeakMap<
  go.Node,
  Map<string, SmartSelectRecommendedOption[]>
>();

const nodeSmartSelectRecommendationStatuses = new WeakMap<
  go.Node,
  Map<string, SmartSelectRecommendationStatus>
>();

const nodeSmartSelectRecommendationCriteria = new WeakMap<
  go.Node,
  Map<string, string>
>();

type SmartSelectRecommendationStatus = 'loading' | 'ready' | 'error';

const getSmartSelectOptionsMap = (node: go.Node) => {
  let optionsMap = nodeSmartSelectRecommendedOptions.get(node);

  if (!optionsMap) {
    optionsMap = new Map<string, SmartSelectRecommendedOption[]>();
    nodeSmartSelectRecommendedOptions.set(node, optionsMap);
  }

  return optionsMap;
};

const getSmartSelectStatusMap = (node: go.Node) => {
  let statusMap = nodeSmartSelectRecommendationStatuses.get(node);

  if (!statusMap) {
    statusMap = new Map<string, SmartSelectRecommendationStatus>();
    nodeSmartSelectRecommendationStatuses.set(node, statusMap);
  }

  return statusMap;
};

export const getCachedSmartSelectOptions = (node: go.Node, portKey: string) =>
  nodeSmartSelectRecommendedOptions.get(node)?.get(portKey) ?? [];

export const getSmartSelectRecommendationStatus = (
  node: go.Node,
  portKey: string,
) => nodeSmartSelectRecommendationStatuses.get(node)?.get(portKey) ?? 'loading';

const getSmartSelectNodeUnitType = (node: go.Node) => {
  const nodeData = node.data as Record<string, unknown>;
  const unitType = nodeData.unitType ?? nodeData.category;

  return isUnitType(unitType) ? unitType : null;
};

const getSmartSelectSuggestionPortIndex = (port: AvailablePort) =>
  port.portIndex === 0 ? 0 : port.portIndex - 1;

export const loadSmartSelectRecommendations = (
  node: go.Node,
  port: AvailablePort,
  updateSmartSelectRecommendedOptions: (node: go.Node) => void,
) => {
  const optionsMap = getSmartSelectOptionsMap(node);
  const statusMap = getSmartSelectStatusMap(node);
  const cacheKey = port.key;
  const topSizeMm = port.outputFlow?.topSize;
  const requiredCapacityTph = port.outputFlow?.totalMass;
  const application =
    store.getState().documentMetadata.flowsheetMetadata?.application ??
    'aggregate';
  const criteriaKey = JSON.stringify([
    application,
    topSizeMm,
    requiredCapacityTph,
  ]);
  let criteriaMap = nodeSmartSelectRecommendationCriteria.get(node);
  if (!criteriaMap) {
    criteriaMap = new Map<string, string>();
    nodeSmartSelectRecommendationCriteria.set(node, criteriaMap);
  }

  if (optionsMap.has(cacheKey) && criteriaMap.get(cacheKey) === criteriaKey) {
    statusMap.set(cacheKey, 'ready');
    updateSmartSelectRecommendedOptions(node);
    return;
  }

  optionsMap.delete(cacheKey);
  criteriaMap.set(cacheKey, criteriaKey);

  const sourceUnitType = getSmartSelectNodeUnitType(node);

  if (!sourceUnitType) {
    statusMap.set(cacheKey, 'error');
    updateSmartSelectRecommendedOptions(node);
    return;
  }

  statusMap.set(cacheKey, 'loading');
  updateSmartSelectRecommendedOptions(node);

  // RTK Query shares the raw request/cache across nodes. The local options map
  // retains each node's equipment-dependent presentation and user selection.
  const suggestionsRequest = store
    .dispatch(
      suggestionsQueryApi.endpoints.getNodeSuggestions.initiate(
        {
          unitType: sourceUnitType,
          portIndex: getSmartSelectSuggestionPortIndex(port),
          application,
          take: SMART_SELECT_SUGGESTION_FETCH_COUNT,
          topSizeMm,
          requiredCapacityTph,
        },
        { subscribe: false },
      ),
    )
    .unwrap()
    .then((suggestions) => {
      const candidates = mapNodeSuggestionsToCandidates(suggestions);
      const options = getSmartSelectRecommendedOptions(
        store.getState().unitInfoDataState.unitInfoData,
        candidates,
        topSizeMm,
        requiredCapacityTph,
      ).slice(0, SMART_SELECT_SUGGESTION_FETCH_COUNT);

      return options;
    });

  void suggestionsRequest
    .then((options) => {
      if (criteriaMap.get(cacheKey) !== criteriaKey) {
        return;
      }
      optionsMap.set(cacheKey, options);
      statusMap.set(cacheKey, 'ready');
      updateSmartSelectRecommendedOptions(node);
    })
    .catch(() => {
      if (criteriaMap.get(cacheKey) !== criteriaKey) {
        return;
      }
      statusMap.set(cacheKey, 'error');
      updateSmartSelectRecommendedOptions(node);
    });
};
